import { useStore } from '@nanostores/react';
import { memo, useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { DnsIssuesSection } from './DnsIssuesSection';
import { DomainEntryItem } from './DomainEntryItem';
import { ConfirmationDialog } from '~/components/dialogs/ConfirmationDialog/ConfirmationDialog.client';
import { Alert } from '~/components/ui/Alert';
import { Button } from '~/components/ui/Button';
import { Link } from '~/components/ui/Link';
import {
  getSSLCertificate,
  listDomainEntries,
  loadDNSStatus,
  removeDomain,
  setPrimaryDomain,
  setSEOBoost,
} from '~/lib/.client/api/netlify';
import { importStore } from '~/lib/projects';
import { domainsStore, type DomainEntry } from '~/lib/stores/domains';
import { projectStore } from '~/lib/stores/project';
import { HELP_CENTER_DOMAINS, HELP_CENTER_HOSTING } from '~/lib/urls';
import { classNames } from '~/utils/classNames';
import { invariant } from '~/utils/invariant';
import { preventUserInteractions } from '~/utils/overlay';
import { withSpinner } from '~/utils/withSpinner';

interface ManageCustomDomainsProps {
  className?: string;
}

// we should do this cleaner, but for now it works to prevent race conditions
let entryVersion = 1;

export const ManageCustomDomains = memo(({ className }: ManageCustomDomainsProps) => {
  const domains = useStore(domainsStore.domainEntries);
  const alertMessage = useStore(domainsStore.alertInManageDomain);

  const [isLoading, setIsLoading] = useState(true);
  const [checkingDns, setCheckingDns] = useState(false);

  const [isSeoBoostEnabled, setIsSeoBoostEnabled] = useState(false);
  const [seoBoostState, setSEOBoostState] = useState<{ loading: boolean; error?: string }>();

  const [isProcessingDomainAction, setIsProcessingDomainAction] = useState(false);
  const [removeDomainEntry, setRemoveDomainEntry] = useState<DomainEntry | undefined>(undefined);
  const [primaryDomainEntry, setPrimaryDomainEntry] = useState<DomainEntry | undefined>(undefined);
  const [domainActionError, setDomainActionError] = useState<string | undefined>(undefined);

  const hasPendingIssues = domains.some((entry) => entry.status === 'not-configured');

  const updateDomainEntries = (entries: DomainEntry[], version?: number) => {
    /**
     * We only update the entries if the version is not set or if it matches the current version. This prevents race
     * conditions where users perform multiple actions that update the entries at the same time.
     */
    if (!version || version === entryVersion) {
      domainsStore.domainEntries.set(entries);

      entryVersion += 1;
    }
  };

  /**
   * TODO: I hate how this currently works. Should clean this mess up after we released it.
   */
  const loadEntriesWithStatus = async (entries?: DomainEntry[] | undefined) => {
    let version = entryVersion;

    const projectId = projectStore.projectId.get();

    invariant(projectId);

    let domainEntries = entries ?? domains;

    /**
     * Only load the domain entries if we don't have any entries yet and the entries where not provided. If we have
     * entries, it means we came from the "Add Custom Domain" dialog and we already fetched the entries.
     */
    if (!entries && domainEntries.length === 0) {
      const projectId = projectStore.projectId.get();

      invariant(projectId, 'Project ID is required to manage domains.');

      const { entries, seoBoost } = await listDomainEntries(projectId);

      updateDomainEntries(entries, version);

      version = entryVersion;

      setIsSeoBoostEnabled(seoBoost);

      domainEntries = entries;
    }

    setIsLoading(false);

    const [entriesWithStatus, certificate] = await Promise.all([
      loadDNSStatus(domainEntries),
      getSSLCertificate(projectId),
    ]);

    // build a regex to match the domains with within the SSL certificate
    const domainAllowedRegex = new RegExp(
      `^${certificate?.domains.map((domain) => domain.replace(/\./g, '\\.').replace(/\*/g, '[^.]*?')).join('|')}$`,
    );

    // update the status of each entry based on the SSL certificate
    for (const entry of entriesWithStatus) {
      if (entry.status === 'complete') {
        if (!certificate) {
          entry.status = 'ssl-error';
        } else {
          // check if the entry is in the ceriticate
          if (domainAllowedRegex.test(entry.domain)) {
            entry.status = 'complete';
          } else {
            entry.status = certificate.renew_running ? 'ssl-pending' : 'ssl-error';
          }
        }
      }
    }

    updateDomainEntries(entriesWithStatus, version);

    // check if we need to show the primary domain alert after loading
    domainsStore.checkDomainAlert();
  };

  const onResolveDNSIssue = useCallback((entry: DomainEntry) => {
    domainsStore.resolveDNSIssue(entry);
  }, []);

  const onRemoveDomain = useCallback((entry: DomainEntry) => {
    setRemoveDomainEntry(entry);
  }, []);

  const onSetAsPrimaryDomain = useCallback((_entry: DomainEntry) => {
    setPrimaryDomainEntry(_entry);
  }, []);

  const onConfirmDomainAction = async () => {
    if (removeDomainEntry || primaryDomainEntry) {
      setIsProcessingDomainAction(true);

      const projectId = projectStore.projectId.get();

      invariant(projectId);

      withSpinner({
        action: async () => {
          try {
            const { entries, siteUrl, isCustomDomain, seoBoost } = removeDomainEntry
              ? await removeDomain(projectId, removeDomainEntry.domain)
              : await setPrimaryDomain(projectId, primaryDomainEntry!.domain);

            setRemoveDomainEntry(undefined);
            setPrimaryDomainEntry(undefined);

            setIsSeoBoostEnabled(seoBoost);

            const deploymentInfo = importStore.get().deploymentInfo;

            if (deploymentInfo) {
              importStore.setKey('deploymentInfo', {
                ...deploymentInfo,
                site_url: siteUrl,
                is_custom_domain: isCustomDomain,
              });
            }

            const entryStatus = new Map(domainsStore.domainEntries.get().map((entry) => [entry.domain, entry.status]));

            // we merge the previous status with the new entries so we don't get the "unknown" status spinner again
            updateDomainEntries(
              entries.map((entry) => ({
                ...entry,
                status: entryStatus.get(entry.domain) ?? entry.status,
              })),
            );

            loadEntriesWithStatus(entries);
          } catch (error: any) {
            setDomainActionError(error.message ?? 'Something went wrong while processing this action.');
          }
        },
        onStateUpdate: setIsProcessingDomainAction,
      });
    }
  };

  const onCancelDomainAction = () => {
    setIsProcessingDomainAction(false);

    setRemoveDomainEntry(undefined);
    setPrimaryDomainEntry(undefined);
    setDomainActionError(undefined);
  };

  const onCheckDnsStatus = useCallback(
    async () =>
      withSpinner({
        action: async () => {
          const reenable = preventUserInteractions();

          try {
            await domainsStore.checkDnsStatus();
          } catch (error) {
            toast.error(error instanceof Error ? error.message : 'Failed to check DNS status.');
          } finally {
            reenable();
          }
        },
        onStateUpdate: setCheckingDns,
      }),
    [setCheckingDns],
  );

  const toggleSEOBoost = async () => {
    try {
      const projectId = projectStore.projectId.get();

      invariant(projectId);

      setSEOBoostState({ loading: true });

      const isEnabled = !isSeoBoostEnabled;

      await setSEOBoost(projectId, isEnabled);

      setIsSeoBoostEnabled(isEnabled);

      setSEOBoostState({ loading: false });
    } catch {
      setSEOBoostState({ loading: false, error: `We couldn't update, try again later` });
    }
  };

  const onAddDomain = useCallback(() => {
    domainsStore.openAddDomainAlias();
  }, []);

  useEffect(() => {
    loadEntriesWithStatus();
  }, []);

  return (
    <div className={classNames('flex flex-col gap-6', className)}>
      <p className="text-bolt-ds-textSecondary">
        You can learn more about custom domain management{' '}
        <Link className="decoration-none" href={HELP_CENTER_DOMAINS} target="_blank">
          here
        </Link>
        .
      </p>

      {isLoading ? (
        <div className="flex items-center justify-center flex-1 pb-8">
          <span className="i-svg-spinners:90-ring-with-bg size-8 inline-block" />
        </div>
      ) : (
        <div className="flex flex-1 min-h-0 flex-col self-stretch gap-6">
          {alertMessage && (
            <Alert
              type="success"
              title={alertMessage}
              className="self-stretch"
              onClose={() => domainsStore.clearAlert()}
            />
          )}

          {hasPendingIssues && <DnsIssuesSection checkingDns={checkingDns} onCheckDnsStatus={onCheckDnsStatus} />}

          <div className="self-stretch min-h-0 flex flex-col overflow-y-auto space-y-2 no-scrollbar">
            {domains.map((domainEntry) => (
              <DomainEntryItem
                key={domainEntry.domain}
                domainEntry={domainEntry}
                onResolveDNSIssue={() => onResolveDNSIssue(domainEntry)}
                onRemove={() => onRemoveDomain(domainEntry)}
                onSetPrimary={() => onSetAsPrimaryDomain(domainEntry)}
              />
            ))}
          </div>

          <div className="flex flex-col gap-1">
            <h2 className="text-sm font-600">
              SEO Boost {isSeoBoostEnabled ? '(Enabled)' : ''}
              {seoBoostState?.error && !seoBoostState.loading && (
                <span className="text-bolt-elements-button-danger-text font-400 ml-2">{seoBoostState.error}</span>
              )}
            </h2>

            <div className="flex justify-between items-center gap-4">
              <p className="max-w-128 text-bolt-ds-textSecondary text-sm">
                Improves how your site appears on Google and social platforms, especially helpful for websites loading
                content dynamically like single page apps.
              </p>

              <Button variant="link-color" onClick={toggleSEOBoost} disabled={seoBoostState?.loading}>
                {seoBoostState?.loading ? 'Loading...' : isSeoBoostEnabled ? 'Disable SEO Boost' : 'Enable SEO Boost'}
              </Button>
            </div>

            <Link
              href={`${HELP_CENTER_HOSTING}#seo-boost`}
              target="_blank"
              className="decoration-none text-[13px] leading-[150%] contents"
            >
              Learn more
            </Link>
          </div>

          <div className="flex gap-2">
            <Button className="h-10" variant="primary" onClick={onAddDomain}>
              Add domain alias
            </Button>
          </div>
        </div>
      )}

      <ConfirmationDialog
        titleText={removeDomainEntry ? 'Remove custom domain' : 'Set primary domain'}
        headerWrapperClassName="gap-6"
        maxWidth={512}
        confirmButtonNode={removeDomainEntry ? 'Remove' : 'Set as primary domain'}
        confirmButtonVariant={removeDomainEntry ? 'danger' : undefined}
        confirmButtonType={removeDomainEntry ? 'tint' : undefined}
        open={Boolean(removeDomainEntry || primaryDomainEntry)}
        isConfirming={isProcessingDomainAction}
        onClose={() => onCancelDomainAction()}
        handleConfirm={() => onConfirmDomainAction()}
      >
        {domainActionError && (
          <Alert type="danger" className="mb-4">
            {domainActionError}
          </Alert>
        )}

        {removeDomainEntry ? (
          <>
            <p>
              Are you sure you want to remove{' '}
              <span className="font-semibold text-bolt-ds-textPrimary">{removeDomainEntry.domain}</span> from this
              project?
            </p>
            {removeDomainEntry?.managedBy === 'CUSTOM' && (
              <Alert type="warning" className="mt-4">
                To keep your subdomains secure, make sure to remove any DNS records pointing to this project’s URL once
                it’s no longer in use.
              </Alert>
            )}
          </>
        ) : (
          <>
            <p>
              Are you sure you want to set{' '}
              <span className="font-semibold text-bolt-ds-textPrimary">{primaryDomainEntry?.domain}</span> as the
              primary domain for this project?
            </p>
          </>
        )}
      </ConfirmationDialog>
    </div>
  );
});

ManageCustomDomains.displayName = 'ManageCustomDomains';
